import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useMemoryHistory, type MemoryRef } from "../../hooks/use-memory-history";
import type { LibraryNode } from "../../../lib/types";

/**
 * Sprint 20 — supersession-chain viewer. Opened from a memory's row menu (or a
 * superseded badge). Shows, oldest → newest: the ancestors this memory replaced,
 * the memory itself, and the descendants that replaced it — with each link's
 * validity window. Answers "how did we get to this fact?".
 */
interface MemoryHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  memory: LibraryNode;
}

function validityLabel(valid_until: string | null): string {
  return valid_until ? "superseded" : "current";
}

export function MemoryHistoryDrawer({ open, onClose, memory }: MemoryHistoryDrawerProps) {
  const navigate = useNavigate();
  const history = useMemoryHistory(memory.id, open);

  if (!open) return null;

  const data = history.data;
  // ancestors come back nearest-first (newest of the older facts); show oldest
  // at the top of the timeline so it reads top→bottom = old→new.
  const ancestors = [...(data?.ancestors ?? [])].reverse();
  const descendants = data?.descendants ?? [];

  function go(id: string) {
    onClose();
    navigate(`/library/${id}`);
  }

  function refRow(ref: MemoryRef, opts: { current?: boolean } = {}) {
    const superseded = !!ref.valid_until;
    return (
      <button
        key={ref.id}
        type="button"
        onClick={() => !opts.current && go(ref.id)}
        style={{
          ...chainItem,
          cursor: opts.current ? "default" : "pointer",
          background: opts.current ? "var(--color-background-info-subtle, var(--color-background-secondary))" : "transparent",
          boxShadow: opts.current ? "inset 0 0 0 1px var(--color-text-info)" : "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <i className={`ti ti-${superseded ? "history" : "bulb"}`} style={{ fontSize: 14, color: "var(--color-text-secondary)", flexShrink: 0 }} aria-hidden="true" />
          <span style={{
            fontSize: 13,
            fontWeight: opts.current ? 600 : 500,
            color: "var(--color-text-primary)",
            textDecoration: superseded ? "line-through" : "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {ref.name}
          </span>
        </span>
        <span style={{ fontSize: 11, color: superseded ? "var(--color-text-warning, var(--color-text-tertiary))" : "var(--color-text-tertiary)", marginLeft: "auto", flexShrink: 0 }}>
          {opts.current ? validityLabel(data?.current_validity.valid_until ?? null) : validityLabel(ref.valid_until)}
        </span>
      </button>
    );
  }

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={drawer} role="dialog" aria-label="Memory history">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)" }}>Memory history</div>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtn}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 16 }}>
          The supersession chain for this fact, oldest at the top.
        </div>

        {history.isLoading ? (
          <div style={hint}>Loading history…</div>
        ) : history.error ? (
          <div style={hint}>Couldn’t load history.</div>
        ) : !data || (ancestors.length === 0 && descendants.length === 0) ? (
          <div style={hint}>
            No supersession history — this memory hasn’t replaced, and hasn’t been replaced by, another.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>
            {ancestors.map((r) => refRow(r))}
            {refRow(
              { id: memory.id, name: memory.name, status: memory.status, valid_from: data.current_validity.valid_from, valid_until: data.current_validity.valid_until },
              { current: true },
            )}
            {descendants.map((r) => refRow(r))}
          </div>
        )}
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 100,
  fontFamily: "var(--font-sans)",
};

const drawer: CSSProperties = {
  background: "var(--color-background-primary)",
  borderLeft: "var(--kuvio-border-default)",
  padding: 24,
  width: 420,
  maxWidth: "92vw",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
};

const chainItem: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  textAlign: "left",
  padding: "10px",
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  fontFamily: "var(--font-sans)",
};

const hint: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  padding: "12px 4px",
};

const closeBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 4,
  display: "flex",
  alignItems: "center",
};
